import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { themes, type Theme } from '@crackpoint/shared/themes';

interface ThemeContextValue {
  theme: Theme;
  themeId: string;
  setTheme: (themeId: string) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

interface ThemeProviderProps {
  children: ReactNode;
  defaultThemeId?: string;
}

export function ThemeProvider({ children, defaultThemeId = 'default' }: ThemeProviderProps) {
  const [themeId, setThemeId] = useState(() => {
    const stored = localStorage.getItem('crackpoint-theme');
    return stored || defaultThemeId;
  });

  const [theme, setTheme] = useState(() => themes[themeId as keyof typeof themes] || themes['crt-dark']);

  useEffect(() => {
    const selectedTheme = themes[themeId as keyof typeof themes];
    if (selectedTheme) {
      setTheme(selectedTheme);
      localStorage.setItem('crackpoint-theme', themeId);
      applyThemeToDOM(selectedTheme);
    }
  }, [themeId]);

  const value: ThemeContextValue = {
    theme,
    themeId,
    setTheme: setThemeId,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

function applyThemeToDOM(theme: Theme): void {
  const root = document.documentElement;

  root.style.setProperty('--color-bg-primary', theme.colours.background.primary);
  root.style.setProperty('--color-bg-secondary', theme.colours.background.secondary);
  root.style.setProperty('--color-bg-tertiary', theme.colours.background.tertiary);
  root.style.setProperty('--color-bg-elevated', theme.colours.background.elevated);

  root.style.setProperty('--color-text-primary', theme.colours.text.primary);
  root.style.setProperty('--color-text-secondary', theme.colours.text.secondary);
  root.style.setProperty('--color-text-tertiary', theme.colours.text.tertiary);
  root.style.setProperty('--color-text-inverse', theme.colours.text.inverse);

  root.style.setProperty('--color-accent-primary', theme.colours.accent.primary);
  root.style.setProperty('--color-accent-secondary', theme.colours.accent.secondary);
  root.style.setProperty('--color-accent-success', theme.colours.accent.success);
  root.style.setProperty('--color-accent-warning', theme.colours.accent.warning);
  root.style.setProperty('--color-accent-error', theme.colours.accent.error);

  root.style.setProperty('--color-feedback-placed', theme.colours.feedback.placed);
  root.style.setProperty('--color-feedback-misplaced', theme.colours.feedback.misplaced);
  root.style.setProperty('--color-feedback-incorrect', theme.colours.feedback.incorrect);

  root.style.setProperty('--spacing-xs', theme.spacing.xs);
  root.style.setProperty('--spacing-sm', theme.spacing.sm);
  root.style.setProperty('--spacing-md', theme.spacing.md);
  root.style.setProperty('--spacing-lg', theme.spacing.lg);
  root.style.setProperty('--spacing-xl', theme.spacing.xl);
  root.style.setProperty('--spacing-xxl', theme.spacing.xxl);

  root.style.setProperty('--font-family', theme.typography.fontFamily);
  root.style.setProperty('--radius-sm', theme.borders.radius.sm);
  root.style.setProperty('--radius-md', theme.borders.radius.md);
  root.style.setProperty('--radius-lg', theme.borders.radius.lg);
  root.style.setProperty('--radius-full', theme.borders.radius.full);

  root.style.setProperty('--transition-fast', theme.transitions.fast);
  root.style.setProperty('--transition-normal', theme.transitions.normal);
  root.style.setProperty('--transition-slow', theme.transitions.slow);
}
